// src/components/Strength/StrengthHeartRateHistogram.js
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import API_BASE_URL from '../../config/api';
import { useTranslation } from '../../i18n';

const StrengthHeartRateHistogram = ({ fileId }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (fileId) {
          const response = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
          if (Array.isArray(response.data.records)) {
            setRecords(response.data.records);
          }
        }
      } catch (error) {
        console.error("❌ Error al obtener datos del archivo FIT:", error);
      }
    };

    fetchData();
  }, [fileId]);

  useLayoutEffect(() => {
    if (!records || records.length === 0) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: root.verticalLayout,
        cursor: am5xy.XYCursor.new(root, { behavior: "zoomX" })
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.DurationAxis.new(root, {
        baseUnit: "second",
        renderer: am5xy.AxisRendererX.new(root, {}),
        tooltip: am5.Tooltip.new(root, {}),
        durationFormat: "hh:mm:ss",
        gridIntervals: [{ timeUnit: "second", count: 600 }]
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        title: am5.Label.new(root, { text: "Frecuencia Cardíaca (bpm)" })
      })
    );

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Frecuencia cardiaca",
        xAxis,
        yAxis,
        valueYField: "heart_rate",
        valueXField: "time",
        stroke: am5.color(0xff0000),
        fill: am5.color(0xff0000),
        tensionX: 0.8,
        strokeWidth: 2,
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueX.formatDuration(\"hh:mm:ss\")}\n{heart_rate} bpm\n{temperature} °C"
        })
      })
    );

    const baseTime = new Date(records[0].timestamp).getTime();
    const filteredRecords = records
      .map((rec) => ({
        time: Math.round((new Date(rec.timestamp).getTime() - baseTime) / 1000),
        heart_rate: rec.heart_rate || 0,
        temperature: rec.temperature || 0
      }))
      .filter((rec) => rec.heart_rate > 0);

    if (filteredRecords.length > 0) {
      xAxis.set("min", 0);
      xAxis.set("max", filteredRecords[filteredRecords.length - 1].time);
      xAxis.set("strictMinMax", true);
    }

    series.data.setAll(filteredRecords);

    series.appear(1000);
    chart.appear(1000, 100);

    const resizeTimer = setTimeout(() => {
      if (root && !root.isDisposed()) {
        window.dispatchEvent(new Event('resize'));
      }
    }, 150);
    return () => {
      clearTimeout(resizeTimer);
      root.dispose();
    };
  }, [records]);

  if (records.length === 0) {
    return <p style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>{t('athleteDashboard.noHRData', 'Sin datos de frecuencia cardíaca por tiempo para esta actividad.')}</p>;
  }

  return (
    <div ref={chartRef} className="chart-container" style={{ width: "100%", height: "300px" }} />
  );
};

export default StrengthHeartRateHistogram;
